import React, { useState } from "react";
import { Button, Typography, MenuItem } from "@mui/material";
import { tss } from "tss-react";
import {
    FolderOpen,
    ArrowRight,
    ArrowDropDown,
    VisibilityOff,
    MoreHoriz,
    DeleteOutline,
    CreateNewFolder,
} from "@mui/icons-material";
import { IconButton, Menu } from "@mui/material";
import EditableLabel from "components/EditableLabel";
import LayersActions from "actions/LayersActions";
import ConfirmationDialog from "components/ConfirmationDialog";

const useStyles = tss.create({
    root: {
        position: "relative",
        display: "flex",
        height: 24,
        width: "100%",
        cursor: "pointer",
        alignItems: "center",
        paddingLeft: 10,
        minWidth: 250,
    },
    title: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        color: "#283C50",
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
    },
    icons: {
        maxWidth: 46,
        marginLeft: -3,
        display: "inline-flex",
    },
    folderRightDiv: {
        position: "absolute",
        display: "inline-flex",
        alignItems: "center",
        right: 12,
        width: 80,
        height: "100%",
    },
    separator: {
        height: "100%",
        borderLeft: "solid 1px #adbbd3",
        display: "inline-flex",
        justifyContent: "center",
    },
    button: {
        minWidth: 20,
        padding: "5px 5px",
    },
    toolIcon: {
        width: 20,
        height: 20,
    },
    grey: {
        minWidth: 16,
        color: "#AAAAAA",
    },
    folderIcon: {
        width: 18,
        height: 18,
        marginRight: 10,
    },
    disabled: {
        opacity: 0.25,
    },
});
type IFolderProps = {
    ID: string;
    name: string;
    level: number;
    children?: any;
    childrenCount?: number;
    defaultStatus?: string;
    disabled?: boolean;
    style?: React.CSSProperties;
    toggleFolderVisibility?: (ID: string) => void;
    renameFolder?: (ID: string, name: string) => void;
    addObjects?: (source?: string) => void;
    updateParent?: () => void;
};

const Folder = (props: IFolderProps) => {
    const { classes } = useStyles();
    const {
        ID,
        name,
        level,
        children,
        childrenCount,
        toggleFolderVisibility,
        defaultStatus,
        renameFolder,
        addObjects,
        updateParent,
        disabled,
    } = props;
    const [status, setStatus] = React.useState(defaultStatus ? defaultStatus : "expand");
    const [anchorEl, setAnchorEl] = useState(null);
    const [openConfirmation, setOpenConfirmation] = useState(false);

    //console.log('Folder.props', props);
    const toggleOpen = () => {
        var state = status === "expand" ? "collapse" : "expand";
        setStatus(state);
        if (toggleFolderVisibility) {
            toggleFolderVisibility(ID);
        }
    };

    const onBlur = (value: string) => {
        if (renameFolder) {
            renameFolder(ID, value);
        }
    };
    const style = {
        ...props.style,
        paddingLeft: level === 0 ? 10 : 10 + level * 11 + "px",
    };
    const handleDragEnter = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        LayersActions.draggedFolder = ID;
    };
    const handleDragLeave = (e: any) => {
        e.preventDefault();
        LayersActions.draggedFolder = "";
    };
    const handleDragOver = (e: any) => {
        e.preventDefault();
    };
    const handleDrop = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        if (LayersActions.draggedFolder !== "") {
            LayersActions.dropFolder(LayersActions.draggedFolder, ID);
        } else {
            LayersActions.moveLayerToFolder(LayersActions.draggedLayerId, ID);
        }
    };
    const showToolTip = (e: any) => {
        var tooltip = document.getElementById("folder-tooltip-" + ID);
        if (tooltip) {
            if (e.type === "mouseover") {
                tooltip.style.display = "inline-flex";
            } else {
                tooltip.style.display = "none";
            }
        }
    };
    const toggleVisibility = () => {
        const visibility = disabled ? "1" : "0";
        LayersActions.toggleVisibility(ID, visibility);
    };

    const handleClose = () => {
        setAnchorEl(null);
        showToolTip({ type: "click" });
    };

    const handleMenu = (evt: any) => {
        setAnchorEl(evt.currentTarget);
    };

    const addObject = () => {
        if (addObjects) {
            addObjects(ID);
        }
    };
    const confirmAction = (object: any) => {
        if (object.status) {
            LayersActions.deleteFolder(ID, () => {
                setOpenConfirmation(false);
                handleClose();
                if (updateParent) {
                    updateParent();
                }
            });
        } else {
            setOpenConfirmation(false);
        }
    };

    const confirmDeleteFolder = () => {
        setOpenConfirmation(true);
    };

    return (
        <div
            draggable={true}
            onDrag={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onMouseOver={showToolTip}
            onMouseOut={showToolTip}
            className={disabled ? classes.disabled : ""}
        >
            <div className={[classes.root, classes.title].join(" ")} style={style}>
                <div className={classes.icons}>
                    <FolderOpen />{" "}
                    {defaultStatus === "expand" ? (
                        <ArrowDropDown onClick={() => toggleOpen()} />
                    ) : (
                        <ArrowRight onClick={() => toggleOpen()} />
                    )}
                </div>
                <EditableLabel
                    initialValue={name + " "}
                    onBlur={onBlur}
                    editable={Boolean(renameFolder)}
                    style={{ paddingLeft: 3 }}
                ></EditableLabel>
                <Typography style={{ paddingLeft: 5 }}> {" (" + childrenCount + ")"}</Typography>
                <div id={"folder-tooltip-" + ID} style={{ display: "none" }} className={classes.folderRightDiv}>
                    <div className={classes.separator} style={{ width: 45 }}>
                        <Button
                            className={!disabled ? classes.button : [classes.button, classes.grey].join(" ")}
                            onClick={() => {
                                toggleVisibility();
                            }}
                        >
                            <VisibilityOff className={classes.toolIcon} />
                        </Button>
                    </div>
                    <div className={classes.separator}>
                        <IconButton
                            size="small"
                            onClick={(e) => handleMenu(e)}
                            style={{ position: "relative", left: 7 }}
                        >
                            <MoreHoriz />
                        </IconButton>
                        {anchorEl ? (
                            <Menu
                                anchorEl={anchorEl}
                                keepMounted
                                aria-autocomplete={"none"}
                                autoFocus={false}
                                disableAutoFocusItem={true}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                                style={{ width: 240, top: 40 }}
                            >
                                <MenuItem onClick={addObject}>
                                    <CreateNewFolder className={classes.folderIcon} /> Add Object to Folder
                                </MenuItem>
                                <MenuItem onClick={confirmDeleteFolder}>
                                    <DeleteOutline className={classes.folderIcon} /> Delete Folder
                                </MenuItem>
                            </Menu>
                        ) : (
                            ""
                        )}
                    </div>
                </div>
            </div>
            <div>{children}</div>
            {openConfirmation ? (
                <ConfirmationDialog
                    open={openConfirmation}
                    title="Delete Folder"
                    type="folder"
                    name={name}
                    confirmAction={confirmAction}
                />
            ) : (
                ""
            )}
        </div>
    );
};

export default Folder;
